import React from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Smartphone, BellRing, UserCheck, BarChart3, ClipboardList } from 'lucide-react';

type FeatureKey = 'iphone' | 'hmc' | 'alerts' | 'contacts' | 'analytics';

const FEATURE_META: Record<FeatureKey, { title: string; description: string; to: string; icon: React.ReactNode } > = {
  iphone: {
    title: "iPhone Monitoring",
    description: "Complete iPhone monitoring hub",
    to: "/features/iphone-monitoring",
    icon: <Smartphone className="w-5 h-5 text-white" />,
  },
  hmc: {
    title: "How's My Child? Report",
    description: "Weekly insights and trends",
    to: "/features/hows-my-child-report",
    icon: <ClipboardList className="w-5 h-5 text-white" />,
  },
  alerts: {
    title: "Smart Alerts",
    description: "AI-powered safety notifications",
    to: "/features/smart-alerts",
    icon: <BellRing className="w-5 h-5 text-white" />,
  },
  contacts: {
    title: "Contact Verification",
    description: "Verify new and unknown contacts",
    to: "/features/contact-verification",
    icon: <UserCheck className="w-5 h-5 text-white" />,
  },
  analytics: {
    title: "Analytics & Social Graph",
    description: "Trends, contacts, and alert categories",
    to: "/features/analytics",
    icon: <BarChart3 className="w-5 h-5 text-white" />,
  },
};

function getOrder(current: FeatureKey): FeatureKey[] {
  // Canonical order with top-left emphasis depending on current page
  const base: FeatureKey[] = ['alerts', 'hmc', 'contacts', 'analytics'];
  if (current === 'iphone') return base;
  // On non-hub pages, show the hub first, then keep the others in a consistent order
  const others = base.filter(k => k !== current);
  return ['iphone', ...others];
}

export default function FeatureCrosslinks({
  current,
  includeGuides = false,
  heading = 'Keep exploring Joey',
  subheading = "Discover related features and next steps",
}: {
  current: FeatureKey;
  includeGuides?: boolean;
  heading?: string;
  subheading?: string;
}) {
  const order = getOrder(current);
  return (
    <section className="py-20 px-[100px] max-md:px-5 bg-muted/20 border-t border-border">
      <div className="container mx-auto max-w-[1200px]" style={{
        backgroundImage: `linear-gradient(rgba(255,255,255,0.4) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.4) 1px, transparent 1px)`,
        backgroundSize: 'clamp(20px, 4vw, 30px) clamp(20px, 4vw, 30px)'
      }}>
        <div className="text-center mb-10">
          <h2 className="text-brand-navy text-5xl font-medium leading-[120%] max-md:text-2xl">{heading}</h2>
          <p className="text-muted-foreground text-subtitle font-normal leading-[160%] max-md:text-base">{subheading}</p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {order.map((key) => {
            const meta = FEATURE_META[key];
            return (
              <Card key={key} className="hover:shadow-lg transition-all duration-300 min-h-[220px] flex flex-col">
                <CardHeader>
                  <div className="flex items-center gap-3">
                    <div className={`w-9 h-9 rounded-md flex items-center justify-center bg-primary`}>{meta.icon}</div>
                    <div>
                      <CardTitle className="leading-tight text-brand-navy">{meta.title}</CardTitle>
                      <CardDescription className="leading-snug">{meta.description}</CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="mt-auto">
                  <Button asChild className="w-full bg-primary hover:brightness-110 text-white">
                    <Link to={meta.to}>Learn More</Link>
                  </Button>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {includeGuides && (
          <div className="mt-10 grid md:grid-cols-2 gap-6">
            <Card className="min-h-[200px] flex flex-col">
              <CardHeader>
                <CardTitle>iMessage Details</CardTitle>
                <CardDescription>Deep dive into iMessage monitoring</CardDescription>
              </CardHeader>
              <CardContent className="mt-auto">
                <Button asChild variant="outline" className="w-full hover:bg-accent">
                  <Link to="/imessage-monitoring-for-parents">Learn More</Link>
                </Button>
              </CardContent>
            </Card>
            <Card className="min-h-[200px] flex flex-col">
              <CardHeader>
                <CardTitle>Articles</CardTitle>
                <CardDescription>Parent guides, conversations, and explainers</CardDescription>
              </CardHeader>
              <CardContent className="mt-auto">
                <Button asChild variant="outline" className="w-full hover:bg-accent">
                  <Link to="/articles">Browse</Link>
                </Button>
              </CardContent>
            </Card>
          </div>
        )}

        <div className="mt-8 flex flex-wrap gap-4 justify-center">
          <Button asChild className="bg-primary hover:brightness-110 text-white">
            <Link to="/pricing">View Pricing</Link>
          </Button>
          {/* Use anchor for hash link to ensure scroll behavior */}
          <a href="/#notification-preview" className="inline-flex items-center justify-center h-10 px-4 py-2 rounded-md border border-input bg-white text-sm font-medium hover:bg-accent hover:text-accent-foreground text-brand-navy">
            See Joey in action
          </a>
        </div>
      </div>
    </section>
  );
}
